/**
 * 消息选择器
 * 根据 Dialogue 的 primaryKey 隔离状态
 * 特殊场景：
 *  会话模块消息存储到全局状态
 *  云信消息的时间处理需要更新状态，由于上下文没有 primaryKey，所以需要存储到全局状态
 */
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily, selectAtom, useAtomCallback } from 'jotai/utils';
import { useCallback, useMemo } from 'react';

type SelectedQueryMsgIds = Map<string, Set<string>>;

const selectedQueryMsgIdsGlobalState = atom<SelectedQueryMsgIds | undefined>(undefined);
const selectedAnswerMsgIdsGlobalState = atom<Set<Conversation.Message['msgId']> | undefined>(
  undefined,
);

// 消息是否可选、范围等状态
const selectedMeta = atomFamily((primaryKey: string) =>
  atom<
    | {
        selectMode?: 'buyer' | 'assistant' | 'both';
        /** 可选范围的时间戳 */
        rangeTimestamp?: number;
      }
    | undefined
  >(undefined),
);
// 客服选择的问题
const selectedQueryMsgIdsState = atomFamily((primaryKey: string) =>
  atom<SelectedQueryMsgIds | undefined>(undefined),
);
// 客服选择的答案
const selectedAnswerMsgIdsState = atomFamily((primaryKey: string) =>
  atom<Set<Conversation.Message['msgId']> | undefined>(undefined),
);

export const useSelectedQueryAIReplyMsgIds = (
  queryMsgId: string,
  primaryKey: string,
  isGlobal: boolean = false,
) => {
  const stateMap = useAtomValue(
    isGlobal ? selectedQueryMsgIdsGlobalState : selectedQueryMsgIdsState(primaryKey),
  );
  return useMemo(() => {
    return stateMap?.get(queryMsgId);
  }, [stateMap, queryMsgId]);
};

export const useSelectedQueryMsgIds = (primaryKey: string, isGlobal: boolean = false) => {
  return useAtomValue(
    isGlobal ? selectedQueryMsgIdsGlobalState : selectedQueryMsgIdsState(primaryKey),
  );
};

export const useSelectedAnswerMsgIds = (primaryKey: string, isGlobal: boolean = false) => {
  return useAtomValue(
    isGlobal ? selectedAnswerMsgIdsGlobalState : selectedAnswerMsgIdsState(primaryKey),
  );
};

export const useSetSelectedQueryMsgIds = (primaryKey: string, isGlobal: boolean = false) => {
  return useSetAtom(
    isGlobal ? selectedQueryMsgIdsGlobalState : selectedQueryMsgIdsState(primaryKey),
  );
};

export const useSetSelectedAnswerMsgIds = (primaryKey: string, isGlobal: boolean = false) => {
  return useSetAtom(
    isGlobal ? selectedAnswerMsgIdsGlobalState : selectedAnswerMsgIdsState(primaryKey),
  );
};

export const useCleanSelectedState = (primaryKey: string) => {
  return () => {
    selectedQueryMsgIdsState.remove(primaryKey);
    selectedAnswerMsgIdsState.remove(primaryKey);
    selectedMeta.remove(primaryKey);
  };
};

export const useClearGlobalSelectedState = () => {
  const setSelectedQueryMsgIdsGlobalState = useSetSelectedQueryMsgIds('', true);
  const setSelectedAnswerMsgIdsGlobalState = useSetSelectedAnswerMsgIds('', true);

  return () => {
    setSelectedQueryMsgIdsGlobalState(undefined);
    setSelectedAnswerMsgIdsGlobalState(undefined);
  };
};

/**
 * 细粒度订阅：判断某条消息是否被选中为问题
 * 使用 selectAtom 实现，只有当该消息的选中状态变化时才会触发重渲染
 */
export const useIsQueryMsgSelected = (
  msgId: string,
  primaryKey: string,
  isGlobal: boolean = false,
) => {
  const baseAtom = isGlobal ? selectedQueryMsgIdsGlobalState : selectedQueryMsgIdsState(primaryKey);

  const selectedAtom = useMemo(
    () => selectAtom(baseAtom, (state) => state?.has(msgId) ?? false),
    [baseAtom, msgId],
  );

  return useAtomValue(selectedAtom);
};

/**
 * 细粒度订阅：判断某条消息是否被选中为答案
 * 使用 selectAtom 实现，只有当该消息的选中状态变化时才会触发重渲染
 */
export const useIsAnswerMsgSelected = (
  msgId: string,
  primaryKey: string,
  isGlobal: boolean = false,
) => {
  const baseAtom = isGlobal
    ? selectedAnswerMsgIdsGlobalState
    : selectedAnswerMsgIdsState(primaryKey);

  const selectedAtom = useMemo(
    () => selectAtom(baseAtom, (state) => state?.has(msgId) ?? false),
    [baseAtom, msgId],
  );

  return useAtomValue(selectedAtom);
};

/**
 * 细粒度订阅：获取已选中问题的数量
 * 用于判断是否达到选择上限
 */
export const useSelectedQueryMsgCount = (primaryKey: string, isGlobal: boolean = false) => {
  const baseAtom = isGlobal ? selectedQueryMsgIdsGlobalState : selectedQueryMsgIdsState(primaryKey);

  const countAtom = useMemo(() => selectAtom(baseAtom, (state) => state?.size ?? 0), [baseAtom]);

  return useAtomValue(countAtom);
};

/**
 * 细粒度订阅：判断已选中问题数量是否达到上限
 * 只有当状态从「未达上限」变为「已达上限」或反过来时才会触发重渲染
 * 这比订阅 count 更细粒度，因为 count 每次变化都会触发重渲染
 */
export const useIsQueryMsgCountReachedLimit = (
  limit: number,
  primaryKey: string,
  isGlobal: boolean = false,
) => {
  const baseAtom = isGlobal ? selectedQueryMsgIdsGlobalState : selectedQueryMsgIdsState(primaryKey);

  const reachedLimitAtom = useMemo(
    () => selectAtom(baseAtom, (state) => (state?.size ?? 0) >= limit),
    [baseAtom, limit],
  );

  return useAtomValue(reachedLimitAtom);
};

export const useIsQueryAIReplyMsgSelected = (
  aiMsgId: string,
  queryMsgId: string,
  primaryKey: string,
  isGlobal: boolean = false,
) => {
  const baseAtom = isGlobal ? selectedQueryMsgIdsGlobalState : selectedQueryMsgIdsState(primaryKey);

  const selectedAtom = useMemo(
    () => selectAtom(baseAtom, (state) => state?.get(queryMsgId)?.has(aiMsgId) ?? false),
    [baseAtom, queryMsgId, aiMsgId],
  );

  return useAtomValue(selectedAtom);
};

export const useSelectedMeta = (primaryKey: string) => {
  return useAtomValue(selectedMeta(primaryKey));
};

export const useSetSelectedMeta = (primaryKey: string) => {
  return useSetAtom(selectedMeta(primaryKey));
};

export const useResetSelectedMeta = (primaryKey: string) => {
  return useAtomCallback(
    useCallback(
      (_, set) => {
        set(selectedMeta(primaryKey), undefined);
      },
      [primaryKey],
    ),
  );
};
